import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Typography, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, CarOutlined } from '@ant-design/icons';
import { Courier, CreateCourierData, UpdateCourierData } from '../types.ts';
import { courierAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Title } = Typography;
const { Search } = Input;

const Couriers: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('couriers');
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCourier, setEditingCourier] = useState<Courier | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();

  const fetchCouriers = async (search?: string) => {
    setLoading(true);
    try {
      const data = await courierAPI.getCouriers(search);
      setCouriers(data);
    } catch (error) {
      message.error('Errore nel caricamento corrieri');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCouriers();
  }, []);

  const handleAdd = () => {
    setEditingCourier(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (courier: Courier) => {
    setEditingCourier(courier);
    form.setFieldsValue({
      company: courier.company,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await courierAPI.deleteCourier(id);
      message.success('Corriere eliminato con successo');
      fetchCouriers(searchTerm);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'eliminazione del corriere');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingCourier) {
        const updateData: UpdateCourierData = values;
        await courierAPI.updateCourier(editingCourier.id, updateData);
        message.success('Corriere aggiornato con successo');
      } else {
        const createData: CreateCourierData = values;
        await courierAPI.createCourier(createData);
        message.success('Corriere creato con successo');
      }
      setModalVisible(false);
      fetchCouriers(searchTerm);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore nell\'operazione';
      message.error(errorMessage);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchCouriers(value);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Azienda',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Data Creazione',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('it-IT'),
    },
    {
      title: 'Ultima Modifica',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleDateString('it-IT'),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_, record: Courier) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Sei sicuro di voler eliminare questo corriere?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>
          <CarOutlined style={{ marginRight: 8 }} />
          Gestione Corrieri
        </Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Nuovo Corriere
          </Button>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Cerca per nome azienda..."
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          style={{ maxWidth: 400 }}
        />
      </div>

      <Table
        columns={canEdit ? columns : columns.filter((c) => c.key !== 'actions')}
        dataSource={couriers}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingCourier ? 'Modifica Corriere' : 'Nuovo Corriere'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="company"
            label="Nome Azienda"
            rules={[{ required: true, message: 'Nome azienda richiesto' }]}
          >
            <Input placeholder="Inserisci il nome dell'azienda corriere" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Couriers;
